import { useNavigate } from 'react-router'
import { Button, Icon, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { Plastic } from '@/features/cards/Plastic'
import { cn } from '@/lib/cn'
import { fromMajor } from '@/lib/money'
import type { Card as CardModel } from '@/data/types'
import { CARD_OPTIONS } from './onboarding-data'
import { STEP_NUMBER, TOTAL_STEPS, useOnboardingDraft, useStep, type CardChoice } from './progress'
import select from './SelectCard.module.css'
import styles from './Onboarding.module.css'

/** The card as it will be issued, so the choice is previewed, not described. */
function preview(choice: CardChoice, name: string): CardModel {
  return {
    id: 'crd_preview' as CardModel['id'],
    kind: choice === 'virtual' ? 'virtual' : 'physical',
    label: 'New card',
    network: 'mastercard',
    last4: '3090',
    status: 'active',
    holder: (name || 'Alex Turner').toUpperCase(),
    expiry: '09/30',
    spentThisMonth: fromMajor(0, 'USD'),
    monthlyLimit: fromMajor(4000, 'USD'),
    wallets: [],
  }
}

const FACTS = [
  { icon: 'shopping_bag', title: 'Monthly purchase limit', value: '$4,000, raise it any time' },
  { icon: 'local_atm', title: 'ATM withdrawals', value: '$200 a month free, then 2%' },
  {
    icon: 'public',
    title: 'Spending abroad',
    value: 'Free in a currency you hold · 0.9% if it converts',
  },
]

/**
 * 11 · Step 5 of 5 — the debit card. It is created here for free and paid for
 * on activation. The agent card is an add-on, offered later.
 */
export function OnboardingCard() {
  useStep('card')
  const navigate = useNavigate()
  const [draft, update] = useOnboardingDraft()
  const options = CARD_OPTIONS.filter((o) => o.id !== 'agent')

  return (
    <FlowLayout
      step={STEP_NUMBER.card}
      totalSteps={TOTAL_STEPS}
      back="/onboarding/accounts"
      footer={
        <>
          <Button
            size="lg"
            block
            iconEnd="arrow_forward"
            onClick={() => navigate('/onboarding/ready')}
          >
            Create my card for free
          </Button>
          <Text variant="caption" tone="secondary">
            Nothing to pay today. You pay the card fee only when you activate it.
          </Text>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            Your debit card
          </Text>
          <Text variant="body" tone="secondary">
            One Mastercard for every account, paying from the balance in the currency you are
            spending. You pay for it when you activate it, not now.
          </Text>
        </Stack>

        <Plastic card={preview(draft.card, draft.name)} />

        <div className={select.options}>
          {options.map((option) => {
            const on = draft.card === option.id
            return (
              <button
                key={option.id}
                type="button"
                className={cn(
                  select.option,
                  on && select.selected,
                  option.id === 'both' && select.wide,
                )}
                onClick={() => update({ card: option.id as CardChoice })}
                aria-pressed={on}
              >
                <Icon name={option.icon} size={22} />
                <Stack gap={1}>
                  <Stack gap={0}>
                    <Text variant="caption" weight="semibold">
                      {option.title}
                    </Text>
                    <Text variant="caption" weight="bold" tone="secondary">
                      {option.price}
                    </Text>
                  </Stack>
                  <Text variant="caption" tone="secondary">
                    {option.body}
                  </Text>
                </Stack>
                <Icon
                  name={on ? 'check_circle' : 'radio_button_unchecked'}
                  size={20}
                  filled={on}
                  className={cn(select.check, on && select.checked)}
                />
              </button>
            )
          })}
        </div>

        <Stack gap={2}>
          {FACTS.map((fact) => (
            <div key={fact.title} className={styles.row}>
              <Icon name={fact.icon} size={20} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold">
                  {fact.title}
                </Text>
                <Text variant="caption" tone="secondary">
                  {fact.value}
                </Text>
              </Stack>
            </div>
          ))}
        </Stack>
      </Stack>
    </FlowLayout>
  )
}
